import type { VotingPeriod } from "@book-club/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { booksKeys } from "@/features/books/queries/booksQueryKeys";
import { votingQueryKeys } from "@/features/voting/queries/votingQueryKeys";
import votingService from "@/features/voting/services/votingService";

type CloseVotingPeriodAndScheduleVariables = {
    bookId: string;
    month: number;
    votingPeriodId: string;
    year: number;
};

/**
 * Mutation hook for closing a voting period and scheduling the winning book
 * Invalidates voting periods and books queries on success
 */
export function useCloseVotingPeriodAndSchedule() {
    const queryClient = useQueryClient();

    return useMutation<VotingPeriod, Error, CloseVotingPeriodAndScheduleVariables>({
        mutationFn: async (variables) => {
            return await votingService.closeVotingPeriodAndSchedule(variables);
        },
        onError: (error) => {
            const errorMessage =
                error instanceof Error ? error.message : "Failed to close voting period and schedule book";
            toast.error(errorMessage);
        },
        onSuccess: () => {
            // Invalidate voting periods query to refresh list (closed period will no longer appear)
            queryClient.invalidateQueries({ queryKey: votingQueryKeys.periods() });

            // Invalidate books query to refresh book data (scheduled book will now have year/month)
            queryClient.invalidateQueries({ queryKey: booksKeys.all });

            toast.success("Voting period closed and book scheduled successfully");
        },
    });
}
